import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Slider, Typography, useMediaQuery } from '@material-ui/core'
import { makeStyles, useTheme } from '@material-ui/core/styles'

const CROP_W = 200
const CROP_H = 300
const EXPORT_W = 160
const EXPORT_H = 240
const MAX_ZOOM_MUL = 4

const useStyles = makeStyles((theme) => ({
    paper: {
        borderRadius: 16,
        overflow: 'hidden',
        maxWidth: 560,
        width: '100%',
        [theme.breakpoints.down('sm')]: {
            margin: 0,
            width: '100%',
            maxHeight: '100%',
            borderRadius: 0,
        },
    },
    title: {
        paddingBottom: 4,
    },
    hint: {
        opacity: 0.72,
        marginTop: 4,
    },
    content: {
        paddingTop: 8,
        [theme.breakpoints.down('sm')]: {
            overflowY: 'auto',
        },
    },
    layout: {
        display: 'flex',
        gap: 20,
        alignItems: 'stretch',
        flexWrap: 'wrap',
        justifyContent: 'center',
    },
    stage: {
        position: 'relative',
        width: 320,
        height: 380,
        maxWidth: '100%',
        borderRadius: 14,
        overflow: 'hidden',
        background: theme.palette.type === 'dark'
            ? 'radial-gradient(circle at 50% 40%, #2a2a2a 0%, #111 70%)'
            : 'radial-gradient(circle at 50% 40%, #ececec 0%, #cfcfcf 70%)',
        cursor: 'grab',
        touchAction: 'none',
        userSelect: 'none',
        boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.08)',
    },
    stageDragging: {
        cursor: 'grabbing',
    },
    image: {
        position: 'absolute',
        pointerEvents: 'none',
        transformOrigin: 'top left',
        willChange: 'transform',
    },
    frame: {
        position: 'absolute',
        left: '50%',
        top: '50%',
        width: CROP_W,
        height: CROP_H,
        marginLeft: -CROP_W / 2,
        marginTop: -CROP_H / 2,
        borderRadius: 12,
        border: '2px solid rgba(255,255,255,0.95)',
        boxShadow: '0 0 0 999px rgba(0,0,0,0.55)',
        pointerEvents: 'none',
    },
    previewCol: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 120,
        gap: 10,
        [theme.breakpoints.down('sm')]: {
            display: 'none',
        },
    },
    previewCard: {
        width: 80,
        height: 120,
        borderRadius: 10,
        overflow: 'hidden',
        position: 'relative',
        boxShadow: '0 8px 24px rgba(0,0,0,0.28)',
        background: theme.palette.primary.main,
    },
    previewImg: {
        position: 'absolute',
        pointerEvents: 'none',
        transformOrigin: 'top left',
    },
    zoomRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        marginTop: 16,
    },
    slider: {
        flex: 1,
    },
}))

interface ICardImageCropDialogProps {
    open: boolean
    imageUrl: string
    onCancel: () => void
    onConfirm: (dataUrl: string) => void
}

function clamp(n: number, min: number, max: number) {
    return Math.min(max, Math.max(min, n))
}

function coverMinZoom(naturalW: number, naturalH: number) {
    return Math.max(CROP_W / naturalW, CROP_H / naturalH)
}

function clampPan(panX: number, panY: number, zoom: number, naturalW: number, naturalH: number) {
    const displayW = naturalW * zoom
    const displayH = naturalH * zoom
    const maxX = Math.max(0, (displayW - CROP_W) / 2)
    const maxY = Math.max(0, (displayH - CROP_H) / 2)
    return {
        x: clamp(panX, -maxX, maxX),
        y: clamp(panY, -maxY, maxY),
    }
}

export default function CardImageCropDialog({ open, imageUrl, onCancel, onConfirm }: ICardImageCropDialogProps) {
    const classes = useStyles()
    const theme = useTheme()
    const isNarrow = useMediaQuery(theme.breakpoints.down('sm'))
    const stageRef = useRef<HTMLDivElement>(null)
    const imgRef = useRef<HTMLImageElement>(null)
    const dragRef = useRef<{ pointerId: number, lastX: number, lastY: number } | null>(null)

    const [natural, setNatural] = useState({ w: 0, h: 0 })
    const [zoom, setZoom] = useState(1)
    const [minZoom, setMinZoom] = useState(1)
    const [pan, setPan] = useState({ x: 0, y: 0 })
    const [dragging, setDragging] = useState(false)

    const resetForImage = useCallback((w: number, h: number) => {
        const nextMin = coverMinZoom(w, h)
        setNatural({ w, h })
        setMinZoom(nextMin)
        setZoom(nextMin)
        setPan({ x: 0, y: 0 })
    }, [])

    useEffect(() => {
        if (!open || !imageUrl) {
            return
        }
        const img = new Image()
        img.onload = () => resetForImage(img.naturalWidth, img.naturalHeight)
        img.src = imageUrl
    }, [open, imageUrl, resetForImage])

    const applyZoom = (nextZoom: number, originX?: number, originY?: number) => {
        const clampedZoom = clamp(nextZoom, minZoom, minZoom * MAX_ZOOM_MUL)
        const ratio = clampedZoom / zoom
        let nextPanX = pan.x
        let nextPanY = pan.y
        if (originX != null && originY != null && stageRef.current) {
            const rect = stageRef.current.getBoundingClientRect()
            const holeLeft = (rect.width - CROP_W) / 2
            const holeTop = (rect.height - CROP_H) / 2
            const px = originX - holeLeft - CROP_W / 2
            const py = originY - holeTop - CROP_H / 2
            nextPanX = px - (px - pan.x) * ratio
            nextPanY = py - (py - pan.y) * ratio
        }
        const clamped = clampPan(nextPanX, nextPanY, clampedZoom, natural.w, natural.h)
        setZoom(clampedZoom)
        setPan(clamped)
    }

    function handlePointerDown(e: React.PointerEvent<HTMLDivElement>) {
        e.currentTarget.setPointerCapture(e.pointerId)
        dragRef.current = { pointerId: e.pointerId, lastX: e.clientX, lastY: e.clientY }
        setDragging(true)
    }

    function handlePointerMove(e: React.PointerEvent<HTMLDivElement>) {
        const drag = dragRef.current
        if (!drag || drag.pointerId !== e.pointerId) {
            return
        }
        const dx = e.clientX - drag.lastX
        const dy = e.clientY - drag.lastY
        drag.lastX = e.clientX
        drag.lastY = e.clientY
        setPan((prev) => clampPan(prev.x + dx, prev.y + dy, zoom, natural.w, natural.h))
    }

    function endDrag(e: React.PointerEvent<HTMLDivElement>) {
        if (dragRef.current && dragRef.current.pointerId === e.pointerId) {
            dragRef.current = null
            setDragging(false)
        }
    }

    function handleWheel(e: React.WheelEvent<HTMLDivElement>) {
        e.preventDefault()
        if (!stageRef.current) {
            return
        }
        const rect = stageRef.current.getBoundingClientRect()
        const factor = e.deltaY > 0 ? 0.92 : 1.08
        applyZoom(zoom * factor, e.clientX - rect.left, e.clientY - rect.top)
    }

    function handleConfirm() {
        const source = imgRef.current
        if (!source || !natural.w) {
            return
        }
        const canvas = document.createElement('canvas')
        canvas.width = EXPORT_W
        canvas.height = EXPORT_H
        const ctx = canvas.getContext('2d')
        if (!ctx) {
            return
        }
        const imgX = CROP_W / 2 + pan.x - (natural.w * zoom) / 2
        const imgY = CROP_H / 2 + pan.y - (natural.h * zoom) / 2
        const sx = -imgX / zoom
        const sy = -imgY / zoom
        const sw = CROP_W / zoom
        const sh = CROP_H / zoom
        ctx.imageSmoothingEnabled = true
        ctx.drawImage(source, sx, sy, sw, sh, 0, 0, EXPORT_W, EXPORT_H)
        onConfirm(canvas.toDataURL('image/jpeg', 0.88))
    }

    const displayW = natural.w * zoom
    const displayH = natural.h * zoom
    const stageW = 320
    const stageH = 380
    const imgLeft = (stageW / 2) + pan.x - displayW / 2
    const imgTop = (stageH / 2) + pan.y - displayH / 2
    const previewScale = 80 / CROP_W
    const previewLeft = (80 / 2) + pan.x * previewScale - displayW * previewScale / 2
    const previewTop = (120 / 2) + pan.y * previewScale - displayH * previewScale / 2
    const zoomValue = minZoom > 0 ? (zoom / minZoom - 1) / (MAX_ZOOM_MUL - 1) : 0

    return (
        <Dialog fullScreen={isNarrow} open={open} onClose={onCancel} maxWidth="sm" classes={{ paper: classes.paper }}>
            <DialogTitle className={classes.title} disableTypography>
                <Typography variant="h6">Crop card image</Typography>
                <Typography variant="body2" className={classes.hint}>
                    Drag to reposition. Scroll or use the slider to zoom.
                </Typography>
            </DialogTitle>
            <DialogContent className={classes.content}>
                <div className={classes.layout}>
                    <div
                        ref={stageRef}
                        className={`${classes.stage}${dragging ? ` ${classes.stageDragging}` : ''}`}
                        onPointerDown={handlePointerDown}
                        onPointerMove={handlePointerMove}
                        onPointerUp={endDrag}
                        onPointerCancel={endDrag}
                        onWheel={handleWheel}
                    >
                        {imageUrl ? (
                            <img
                                ref={imgRef}
                                className={classes.image}
                                src={imageUrl}
                                alt=""
                                draggable={false}
                                style={{
                                    width: natural.w || undefined,
                                    height: natural.h || undefined,
                                    transform: `translate(${imgLeft}px, ${imgTop}px) scale(${zoom})`,
                                }}
                            />
                        ) : null}
                        <div className={classes.frame} />
                    </div>
                    <div className={classes.previewCol}>
                        <Typography variant="caption">Preview</Typography>
                        <div className={classes.previewCard}>
                            {imageUrl && natural.w ? (
                                <img
                                    className={classes.previewImg}
                                    src={imageUrl}
                                    alt=""
                                    draggable={false}
                                    style={{
                                        width: natural.w,
                                        height: natural.h,
                                        transform: `translate(${previewLeft}px, ${previewTop}px) scale(${zoom * previewScale})`,
                                    }}
                                />
                            ) : null}
                        </div>
                    </div>
                </div>
                <div className={classes.zoomRow}>
                    <Typography variant="caption">Zoom</Typography>
                    <Slider
                        className={classes.slider}
                        value={Number.isFinite(zoomValue) ? zoomValue : 0}
                        min={0}
                        max={1}
                        step={0.01}
                        onChange={(_, value) => {
                            const t = Array.isArray(value) ? value[0] : value
                            applyZoom(minZoom * (1 + t * (MAX_ZOOM_MUL - 1)))
                        }}
                    />
                </div>
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel}>Cancel</Button>
                <Button color="primary" variant="contained" onClick={handleConfirm} disabled={!natural.w}>
                    Use image
                </Button>
            </DialogActions>
        </Dialog>
    )
}
